import { createContext, useContext, ReactNode, useState, useEffect } from "react";
import Axios from "axios";
import { axios } from "@/lib/axios";
import storage from "@/utils/storage";

interface AuthUser {
  uid?: string;
  email: string;
  displayName?: string;
  role: string;
}

interface AuthContextProps {
  user?: AuthUser;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => void;
  loading?:boolean
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

interface ResponseMessage {
  result: boolean;
  role?: number;
}
interface UserProfileResponse {
  success: boolean;
  user: {
    email: string;
    firstname?: string;
    lastname?: string;
    role?: string;
  };
  searchMethod: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = storage.getToken();
    const email = storage.getEmail();
    if (token && email) {
      axios
        .get<UserProfileResponse>("/users/get_user_profile?email=" + email)
        .then((res) => {

          if (res.data.success && res.data.user) {
            let role = res?.data?.user?.role;
            const isAdmin = !isNaN(Number(role)) && Number(role) >= 1;
            if (!isAdmin) return
            setUser({
              email: res.data.user.email,
              displayName: `${res.data.user.firstname ?? ""} ${res.data.user.lastname ?? ""}`,
              role: "ADMIN",
            });
          }
        })
        .catch(() => {
          storage.clearAuth();
          setUser(undefined);
        }).finally(() => { setLoading(false); });
    } else {
      setLoading(false);
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    const wpUrl =
      process.env.REACT_WP_JWT_URL ??
      "https://ericcressey.com/wp-json/jwt-auth/v1/token";

    try {
      const isAdmin = (await axios.post("/users/signin_admin", { email })) as ResponseMessage;
      if (isAdmin.result === false) {
        return Promise.reject({
          message: "Only admins can access to this site.",
        });
      }

      const response = await Axios.post(wpUrl, {
        username: email,
        password,
      });

      const { token, user_email, user_display_name } = response.data;
      storage.setAuth(token, user_email);

      setUser({
        email: user_email,
        displayName: user_display_name,
        role: "ADMIN",
      });

      return true;
    } catch (err: any) {
      return Promise.reject(err.response?.data?.message || "Login failed");
    }
  };

  const signOut = () => {
    storage.clearAuth();
    setUser(undefined);
  };

  const value: AuthContextProps  & { loading: boolean } = {
    user,
    signIn,
    signOut,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
